import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WaitlistForm } from '../components/ui/waitlist-form';

const SEO_LANGS = ['en', 'uk', 'es', 'fr', 'pl', 'it', 'pt'];
const PATH = 'ai-course-builder';

export const CourseBuilderPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [1, 2, 3].map((n) => ({
    title: t(`builderPage.step${n}Title`),
    description: t(`builderPage.step${n}Description`)
  }));

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Helmet>
        <title>{t('builderPage.seoTitle')}</title>
        <meta name="description" content={t('builderPage.seoDescription')} />
        <link rel="canonical" href={`https://scormate.com/${currentLang}/${PATH}`} />
        {SEO_LANGS.map((code) => (
          <link key={code} rel="alternate" hrefLang={code} href={`https://scormate.com/${code}/${PATH}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`https://scormate.com/en/${PATH}`} />
        <meta property="og:title" content={t('builderPage.seoTitle')} />
        <meta property="og:description" content={t('builderPage.seoDescription')} />
        <meta property="og:locale" content={currentLang} />
      </Helmet>
      <Header />
      <main id="main" className="flex-grow" role="main">
        <section className="border-b border-border bg-panel">
          <div className="mx-auto flex max-w-[1180px] flex-col gap-5 px-4 py-10 sm:px-8 sm:py-14">
            <span className="self-start rounded border border-border px-2 py-1 text-[11px] font-medium text-primary">
              {t('builder.eyebrow')}
            </span>
            <h1 className="max-w-[22ch] text-[32px] font-semibold leading-[1.1] tracking-[-0.025em] [text-wrap:balance] sm:text-[42px]">
              {t('builder.title')}
            </h1>
            <p className="max-w-[60ch] text-base text-muted-foreground [text-wrap:pretty]">
              {t('builder.description')}
            </p>
            {/* Until the builder opens to everyone, this page collects early-access emails like the homepage. */}
            <WaitlistForm source="builder-page" currentLang={currentLang} />
            <p className="text-xs text-faint">
              {t('builder.note')} {t('builderPage.beta')}
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-[1180px] px-4 py-14 sm:px-8">
          <h2 className="mb-6 text-2xl font-semibold tracking-[-0.02em]">{t('howItWorks.title')}</h2>
          <ol className="grid gap-4 md:grid-cols-3">
            {steps.map((step, index) => (
              <li key={step.title} className="flex flex-col gap-2 rounded-md border border-border bg-panel p-[18px]">
                <span className="font-mono text-[11px] text-primary">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="text-[15px] font-semibold">{step.title}</h3>
                <p className="text-muted-foreground [text-wrap:pretty]">{step.description}</p>
              </li>
            ))}
          </ol>
        </section>
      </main>
      <Footer />
    </div>
  );
};
